/**
 * @fileoverview Teacher Signup page wrapper
 * @description Main component with two-column layout
 */

import { useForceEnglish, useSpecificFeature } from 'hooks'
import { ArcAuthHeader } from 'components/ArcAuthHeader'
import { ArcAuthShell } from 'components/ArcAuthPage'
import { ArcHeroDisc } from 'components/ArcHeroDisc'
import { NotificationProvider } from 'context'
import { SchoolLogoColor } from '@legacy/common/types/whitelabel'
import Logo from '@legacy/common/components/shared/Logo'
import { LumiarToastContainer } from '@legacy/common/_components/LumiarToast'
import IllustrationSrc from '@legacy/assets/images/signup-illustration.webp'
import { SignupProvider, useSignupContext } from './context/SignupContext'
import { SignupStep1 } from './components/SignupStep1'
import { SignupStep2 } from './components/SignupStep2'
import { SignupStep3 } from './components/SignupStep3'
import {
  PageContainer,
  LogoContainer,
  ContentWrapper,
  IllustrationColumn,
  IllustrationImage,
  FormColumn,
  ArcContentWrapper,
  ArcIllustrationColumn,
  ArcFormCard,
} from './styles'

/**
 * Renders the current step based on context
 */
const SignupSteps = () => {
  const { step } = useSignupContext()

  switch (step) {
    case 1:
      return <SignupStep1 />
    case 2:
      return <SignupStep2 />
    case 3:
      return <SignupStep3 />
    default:
      return <SignupStep1 />
  }
}

/**
 * Teacher Signup page content with two-column layout
 */
const SignupContent = () => {
  // Flipper endpoint: works unauthenticated; rendering waits for the flag so
  // neither branding flashes (AR-792). Flag off keeps the legacy page untouched.
  const { enabled: isArcEnabled, loading: isFlagLoading } =
    useSpecificFeature('arc-mosaico-project')

  if (isFlagLoading) return null

  if (isArcEnabled) {
    return (
      <ArcAuthShell>
        <ArcAuthHeader />
        <ArcContentWrapper>
          <ArcIllustrationColumn>
            <ArcHeroDisc />
          </ArcIllustrationColumn>
          <ArcFormCard>
            <SignupSteps />
          </ArcFormCard>
        </ArcContentWrapper>
      </ArcAuthShell>
    )
  }

  return (
    <PageContainer>
      <LogoContainer>
        <Logo color={SchoolLogoColor.Primary} />
      </LogoContainer>
      <ContentWrapper>
        <IllustrationColumn>
          <IllustrationImage src={IllustrationSrc} alt='Signup illustration' />
        </IllustrationColumn>
        <FormColumn>
          <SignupSteps />
        </FormColumn>
      </ContentWrapper>
    </PageContainer>
  )
}

/**
 * Teacher Signup page wrapper with providers
 */
export const TeacherSignup = () => {
  useForceEnglish()

  return (
    <NotificationProvider>
      <SignupProvider>
        <LumiarToastContainer />
        <SignupContent />
      </SignupProvider>
    </NotificationProvider>
  )
}
